import { z } from 'zod';
import { CouleurSchema, TexteBilingueSchema } from './textes.js';
/** Les trois thèmes standard dérivés des maquettes `designs/` (section 3.4). */
export const THEMES = ['cimaise', 'cartel', 'accrochage'];
export const NomThemeSchema = z.enum(THEMES);
/**
 * Polices ouvertes à la charte d'une galerie. Chaque thème a les siennes par
 * défaut (2.4) ; la charte peut en choisir une autre dans cette liste. Toutes
 * sont sous licence ouverte et **auto-hébergées** (D12) : aucune requête vers
 * Google Fonts en production.
 */
export const POLICES = [
    'familjen-grotesk',
    'newsreader',
    'archivo',
    'syne',
    'karla',
    'dm-serif-display',
    'dm-sans',
    'ibm-plex-mono',
];
export const NomPoliceSchema = z.enum(POLICES);
const woff2 = (nom, suffixe, graisse, style) => ({
    chemin: `/fonts/${nom}-${suffixe}.woff2`,
    graisse,
    style,
});
/** Catalogue des polices : familles, replis et fichiers à copier. */
export const CATALOGUE_POLICES = {
    'familjen-grotesk': {
        famille: 'Familjen Grotesk',
        repli: '"Helvetica Neue", Arial, sans-serif',
        fichiers: [
            woff2('familjen-grotesk', '400', 400, 'normal'),
            woff2('familjen-grotesk', '500', 500, 'normal'),
            woff2('familjen-grotesk', '600', 600, 'normal'),
            woff2('familjen-grotesk', '400-italic', 400, 'italic'),
        ],
    },
    newsreader: {
        famille: 'Newsreader',
        repli: 'Georgia, "Times New Roman", serif',
        fichiers: [
            woff2('newsreader', '400', 400, 'normal'),
            woff2('newsreader', '400-italic', 400, 'italic'),
        ],
    },
    archivo: {
        famille: 'Archivo',
        repli: '"Helvetica Neue", Arial, sans-serif',
        fichiers: [woff2('archivo', '400', 400, 'normal'), woff2('archivo', '600', 600, 'normal')],
    },
    syne: {
        famille: 'Syne',
        repli: '"Helvetica Neue", Arial, sans-serif',
        fichiers: [woff2('syne', '400', 400, 'normal'), woff2('syne', '700', 700, 'normal')],
    },
    karla: {
        famille: 'Karla',
        repli: '"Helvetica Neue", Arial, sans-serif',
        fichiers: [
            woff2('karla', '400', 400, 'normal'),
            woff2('karla', '500', 500, 'normal'),
            woff2('karla', '400-italic', 400, 'italic'),
        ],
    },
    'dm-serif-display': {
        famille: 'DM Serif Display',
        repli: 'Georgia, "Times New Roman", serif',
        fichiers: [woff2('dm-serif-display', '400', 400, 'normal')],
    },
    'dm-sans': {
        famille: 'DM Sans',
        repli: '"Helvetica Neue", Arial, sans-serif',
        fichiers: [
            woff2('dm-sans', '400', 400, 'normal'),
            woff2('dm-sans', '500', 500, 'normal'),
            woff2('dm-sans', '600', 600, 'normal'),
        ],
    },
    'ibm-plex-mono': {
        famille: 'IBM Plex Mono',
        repli: '"SFMono-Regular", Menlo, monospace',
        fichiers: [
            woff2('ibm-plex-mono', '400', 400, 'normal'),
            woff2('ibm-plex-mono', '500', 500, 'normal'),
        ],
    },
};
/** Blocs de la page d'accueil, ordonnés par la charte (section 3.4). */
export const BlocAccueilSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('exposition-en-cours') }),
    z.object({ type: z.literal('oeuvres'), limite: z.number().int().min(1).max(24) }),
    z.object({ type: z.literal('boutique'), limite: z.number().int().min(1).max(24) }),
    z.object({ type: z.literal('artistes') }),
    z.object({ type: z.literal('agenda'), limite: z.number().int().min(1).max(12) }),
    z.object({ type: z.literal('actualites'), limite: z.number().int().min(1).max(12) }),
    z.object({ type: z.literal('newsletter') }),
    z.strictObject({ type: z.literal('prise-rendez-vous') }),
    z.object({ type: z.literal('evenements'), limite: z.number().int().min(1).max(12) }),
    z.object({
        type: z.literal('texte'),
        titre: TexteBilingueSchema,
        corps: TexteBilingueSchema,
    }),
]);
export const CharteSchema = z.object({
    theme: NomThemeSchema,
    /** Logotype de la galerie ; à défaut, le nom est composé typographiquement. */
    logotypeUrl: z.string().startsWith('/').optional(),
    logotypeHauteur: z.number().int().positive().max(200).optional(),
    /** Les deux couleurs de la charte, injectées en variables CSS. */
    couleurPrincipale: CouleurSchema,
    couleurSecondaire: CouleurSchema,
    policeTitres: NomPoliceSchema,
    policeTexte: NomPoliceSchema,
    /** Disposition de l'accueil : la liste ordonnée des blocs à composer. */
    accueil: z.array(BlocAccueilSchema).min(1),
});
/** Les polices réellement employées par une charte, sans doublon. */
export function policesDeLaCharte(charte) {
    return charte.policeTitres === charte.policeTexte
        ? [charte.policeTitres]
        : [charte.policeTitres, charte.policeTexte];
}
//# sourceMappingURL=charte.js.map